import { useLanguage, useLanguages } from '@api/application-configuration';
import { Check, Globe2 } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Image } from '@unistocks/components/basic/image';
import { Menu } from '@unistocks/components/menu/menu';
import { FLAGS, langToFlag } from '@unistocks/features/portfolios/flags';
import { t } from '@unistocks/localization';
import { useProfileUpdateSettingsMutation } from '@unistocks/proxy/axios-client/ProfileQuery';
import { APP_CONFIG_QUERY_KEY } from '@unistocks/shared/hooks/use-application-configuration';
import { UnistocksSettings } from '@unistocks/shared/settings';
import { SelectOption } from '@unistocks/types';
import React, { PropsWithChildren, useState } from 'react';
import { Spinner } from 'tamagui';

export type LanguageSelectProps = PropsWithChildren;

/**
 * Selects the current language of the application and saves it to the user's profile.
 */
export const LanguageSelect: React.FC<LanguageSelectProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const languages = useLanguages();
  const selectedLanguage = useLanguage();
  const [open, setOpen] = useState(false);

  const items: SelectOption[] =
    languages?.map(lang => ({
      key: lang.displayName,
      value: lang.cultureName,
      icon: (
        <Image
          rounded
          size={'$1.5'}
          source={FLAGS[langToFlag[lang.twoLetterISOLanguageName]]}
          alt={`${lang.displayName} flag`}
        />
      )
    })) ?? [];

  const { mutateAsync, isPending, variables } = useProfileUpdateSettingsMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_CONFIG_QUERY_KEY });
      setOpen(false);
    }
  });

  const pendingLanguage = variables?.settings[UnistocksSettings.Language];

  return (
    <Menu
      open={open}
      onOpenChange={setOpen}
      trigger={children}
      headerTitle={t('::SelectWhat', { 0: t('::Language').toLowerCase() })}
      headerIcon={<Globe2 color={'$success'} />}
    >
      {items.map(item => {
        const isSelected = item.value === selectedLanguage;
        const isSaving = isPending && item.value === pendingLanguage;

        return (
          <Menu.Item
            key={item.key}
            closeOnPress={false}
            disabled={isPending}
            icon={item.icon}
            iconAfter={
              isSaving ? (
                <Spinner color={'$color'} size={'small'} />
              ) : isSelected ? (
                <Check color={'$success'} />
              ) : undefined
            }
            title={item.title ?? item.key}
            onPress={() =>
              mutateAsync({
                settings: {
                  [UnistocksSettings.Language]: item.value
                }
              })
            }
          />
        );
      })}
    </Menu>
  );
};

LanguageSelect.displayName = 'LanguageSelect';
